// --------------------------------------------------------
// AGENDA PREVIEW DIA - AgendaPreviewDia
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import moment from 'moment';
import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import CriarAgendaHeader from '../../components/criarAgendaHeader/CriarAgendaHeader';
import GenericModal from '../../components/genericModal/GenericModal';
import { GenericSolidButton } from '../../components/UI/buttons/Button.styled';
import ContainerWithSidebar from '../../components/UI/containerWithSidebar/ContainerWithSidebar';
import { H1, Text, TextLink } from '../../components/UI/generalLayout/Layout.styled';
import Colors from '../../constants/Colors';
import { Paths } from '../../enums/Paths';
import { GeneralContainer } from '../home/Home.styled';
import { ReactComponent as Block } from '../../assets/imgs/protectionLock.svg'
import { ReactComponent as TrashBin } from '../../assets/imgs/trashBin.svg'
import { ReactComponent as Unblock } from '../../assets/imgs/protectionLockOpen.svg'
import { getDateDescription } from '../../helpers/HelperFunctions';
import useResponsiveInfo from '../../hooks/useResponsiveInfo';
import { Tooltip } from '@mui/material';
import { CompanyListContainer } from '../company/CompanyList.styled';
import { StyledCheckbox } from '../../components/criarAgenda/AgendaMesHorarioListItem.styled';

// --------------------------------------------------------
// AGENDA PREVIEW DIA INTERFACE
// --------------------------------------------------------
function AgendaPreviewDia(): JSX.Element {
    const { state }: any = useLocation();
    const { nomeProfissional, agenda, daysList, dayIndex, savedState } = state;
    const [slots, setSlots] = useState<any[]>(daysList[dayIndex].slots || [])
    const [selectedSlotsIndices, setSelectedSlotsIndices] = useState<number[]>([])
    const [showModalActionAll, setShowModalActionAll] = useState(false)
    const [actionType, setActionType] = useState<'block' | 'delete' | null>(null)
    const navigate = useNavigate();
    const { isMobile } = useResponsiveInfo();

    const onBack = (updatedSlots?: any[]) => {
        const newDaysList = [...daysList];
        if (updatedSlots) {
            newDaysList[dayIndex] = { ...newDaysList[dayIndex], slots: updatedSlots };
        }
        return navigate(Paths.CriarAgenda, { 
            state: { 
                nomeProfissional, 
                agenda, 
                updatedDaysList: newDaysList,
                savedState
            } 
        })
    }

    const toggleSelectSlot = (index: number) => {
        setSelectedSlotsIndices(prev => {
            if (prev.includes(index)) {
                return prev.filter(i => i !== index);
            } else {
                return [...prev, index];
            }
        })
    }

    const toggleSelectAll = () => {
        if (selectedSlotsIndices.length === slots.length) {
            setSelectedSlotsIndices([]);
        } else {
            setSelectedSlotsIndices(slots.map((_, i) => i));
        }
    }

    const onActionSelected = (type: 'block' | 'delete') => {
        setActionType(type);
        setShowModalActionAll(true);
    }

    const confirmActionSelected = () => {
        const newSlots = [...slots];
        selectedSlotsIndices.forEach(index => {
            if (actionType === 'block') {
                newSlots[index].inativo = !newSlots[index].inativo;
            } else if (actionType === 'delete') {
                newSlots[index].excluido = true;
            }
        });
        setSlots(newSlots.filter(s => !s.excluido));
        setSelectedSlotsIndices([]);
        setShowModalActionAll(false);
    }

    const toggleBlockSlot = (index: number) => {
        const newSlots = [...slots];
        newSlots[index].inativo = !newSlots[index].inativo;
        setSlots(newSlots);
    }

    const deleteSlot = (index: number) => {
        const newSlots = [...slots];
        newSlots[index].excluido = true;
        setSlots(newSlots.filter(s => !s.excluido));
    }

    return (
        <ContainerWithSidebar>
            <GeneralContainer>
                <TextLink color={Colors.SPM600} onClick={() => onBack(slots)}>{'< Voltar para criar agenda'}</TextLink>
                <CriarAgendaHeader nome={nomeProfissional} agenda={agenda} hasRightButton={false} errors={[]} onCloseError={() => { }} />
                
                <div style={{ flexDirection: isMobile ? 'column' : 'row', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <H1>{getDateDescription(daysList[dayIndex].date)}</H1>
                    <div style={{ flexDirection: 'row', display: 'flex' }}>
                        <GenericSolidButton 
                            color={Colors.SPM600} 
                            disabled={selectedSlotsIndices.length === 0} 
                            style={{ marginRight: 10 }}
                            onClick={() => onActionSelected('block')}
                        >
                            Bloquear selecionados
                        </GenericSolidButton>
                        <GenericSolidButton 
                            color={Colors.error} 
                            disabled={selectedSlotsIndices.length === 0}
                            onClick={() => onActionSelected('delete')}
                        >
                            Excluir selecionados
                        </GenericSolidButton>
                    </div>
                </div>

                <div style={{ flexDirection: 'row', display: 'flex', width: '100%', alignItems: 'center', backgroundColor: Colors.GRAY50, padding: '10px 0' }}>
                    <StyledCheckbox 
                        checked={selectedSlotsIndices.length === slots.length && slots.length > 0} 
                        onChange={toggleSelectAll} 
                    />
                    <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', paddingRight: 20 }}>
                        <Text bold style={{ width: '15%' }}>Data</Text>
                        <Text bold style={{ width: '15%' }}>Hora</Text>
                        <Text bold style={{ width: '50%' }}>Unidade</Text>
                        <Text bold style={{ width: '10%', textAlign: 'right' }}>Ações</Text>
                    </div>
                </div>

                <CompanyListContainer>
                    {slots.map((slot, index) => (
                        <div key={index} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '10px 0', 
                            borderBottom: `1px solid ${Colors.GRAY200}`,
                            backgroundColor: slot.inativo ? Colors.GRAY100 : 'transparent'
                        }}>
                            <StyledCheckbox 
                                checked={selectedSlotsIndices.includes(index)} 
                                onChange={() => toggleSelectSlot(index)} 
                            />
                            <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', paddingRight: 20, alignItems: 'center' }}>
                                <Text style={{ width: '15%', opacity: slot.inativo ? 0.5 : 1 }}>{daysList[dayIndex].date}</Text>
                                <Text style={{ width: '15%', opacity: slot.inativo ? 0.5 : 1 }}>
                                    {`${moment(slot.horario).format('HH:mm')} - ${moment(slot.horario).add(Number(daysList[dayIndex].horario.tempoConsulta.id), 'm').format('HH:mm')}`}
                                </Text>
                                <Text style={{ width: '50%', opacity: slot.inativo ? 0.5 : 1 }}>{savedState.unitSelected.nome}</Text>
                                <div style={{ width: '10%', display: 'flex', justifyContent: 'flex-end' }}>
                                    <Tooltip title={slot.inativo ? 'Desbloquear' : 'Bloquear'}>
                                        <div onClick={() => toggleBlockSlot(index)} style={{ cursor: 'pointer', marginRight: 10 }}>
                                            {slot.inativo ? <Unblock /> : <Block />}
                                        </div>
                                    </Tooltip>
                                    <Tooltip title="Excluir">
                                        <div onClick={() => deleteSlot(index)} style={{ cursor: 'pointer' }}>
                                            <TrashBin />
                                        </div>
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                    ))}
                </CompanyListContainer>

                <GenericModal
                    visible={showModalActionAll}
                    Icon={() => actionType === 'block' ? <Block width="120" height="120" /> : <TrashBin width="120" height="120" />}
                    onCancel={confirmActionSelected}
                    onConfirm={() => setShowModalActionAll(false)}
                    modalTitle={`Tem certeza que deseja ${actionType === 'block' ? 'bloquear/desbloquear' : 'excluir'} os horários selecionados?`}
                    modalSubtitle={''}
                    cancelButtonMsg={'Não'}
                    confirmButtonMsg={'Sim'}
                />

            </GeneralContainer>
        </ContainerWithSidebar>
    )
}

export default AgendaPreviewDia;
